import Image from '@/components/common/Image/Image'
import { ResetIcon } from '@/components/icons'
import Refresh from '@/components/icons/Refresh'
import { formatDecoderValue } from '@/constant/decoder'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import { usePermission } from '@/hooks/permissions'
import useSearchStationOff from '@/features/config/hooks/controller/useSearchStationOff'
import useErrorcode from '@/features/config/hooks/station/useErrorCode'
import useStationLineUnitControl from '@/features/config/hooks/station/useStationLineUnitControl'
import useStationLineUnitDecoder from '@/features/config/hooks/station/useStationLineUnitDecoder'
import useStationResetById from '@/features/config/hooks/station/useStationResetById'
import { useDateTime } from '@/hooks/useDateTime'
import { useUnits } from '@/hooks/useUnits'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { ReactNode, useEffect, useRef, useState, useMemo } from 'react'
import { HubConnection } from '@microsoft/signalr'
import toast from 'react-hot-toast'
import useTranslation from '@/hooks/useTranslation'
import { useSignalR } from '@/signalr/context'
import { ActionSignalR, getGroupName } from '@/types'
import {
  StationUpdateSignalR,
  stationUpdateItemId,
} from '@/types/SignalR/station'
import { STATION_STATE_NAME } from '@/types/stations'
import { resolveStationName } from '@/utils/stationName'

// Redux imports
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchStationOverview,
  resetStationOverview,
  stationOverviewState,
} from '@store/slices/StationOverview/stationOverviewSlice'

const LOCAL_KEY_ERROR = 'id-error'

const infoBoxStyle = {
  padding: '20px 0 15px',
  borderTop: '1px solid rgba(215, 215, 215, 1)',
  '&.stt-ovv--color p:not(.stt-ovv__infobox__title)': {
    color: 'primary.main',
  },
  '& .stt-ovv__infobox__row': {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    justifyContent: 'space-between',
  },
  '& .stt-ovv__infobox__row:not(:last-child)': {
    marginBottom: '10px',
  },
  '& p': {
    lineHeight: '1.3',
    color: 'black',
    fontSize: '16px',
    fontWeight: '400',
  },
  '& p.stt-ovv__infobox__title': {
    maxWidth: '60%',
  },
  '& p.stt-ovv__infobox__title_error': {
    color: '#FF3030',
  },
}

interface StationOverviewProps {
  id?: string
}

function formatTime(totalSeconds: number) {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const formattedHours = String(hours).padStart(2, '0')
  const formattedMinutes = String(minutes).padStart(2, '0')
  const formattedSeconds = String(seconds).padStart(2, '0')

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
}

const StationOverview = ({ id }: StationOverviewProps) => {
  const { t } = useTranslation()

  const { closeSidebar, openSidebar, setSidebarView, openModal, setModalView } =
    useConfigUI()
  const { interfaceId } = useAuthContext()
  const { getHubConnect, joinGroup, leaveGroup } = useSignalR()
  const { formatDateByTimezone } = useDateTime()
  const { convertLabelUnit, convertFromMetric } = useUnits()
  const { isViewOnly } = usePermission()
  const [dateUpdateControl, setDateControl] = useState<Date | null>(null)
  const [dateUpdateDecode, setDateDecoder] = useState<Date | null>(null)
  // Optimistic On/Off state — updated immediately on API success, cleared when Redux syncs
  const [optimisticStatus, setOptimisticStatus] = useState<'On' | 'Off' | null>(null)

  const dispatch = useDispatch()
  const { data, loading } = useSelector(stationOverviewState)

  // fetch initial + refresh on id/interfaceId change
  useEffect(() => {
    if (!id || !interfaceId) return

    // Reset previous station data to avoid showing stale data
    dispatch(resetStationOverview())
    setOptimisticStatus(null)

    // Initial fetch
    dispatch(fetchStationOverview({ interfaceId, stationId: id }))

    // Polling every 30s
    const intervalId = setInterval(() => {
      dispatch(fetchStationOverview({ interfaceId, stationId: id }))
    }, 30000)

    return () => clearInterval(intervalId)
  }, [id, interfaceId, dispatch])

  const {
    data: dataLineUnitControl,
    isFetching: loadingControl,
    refetch: fetchControl,
  } = useStationLineUnitControl(
    Number(interfaceId),
    Number(data?.controller?.address) || 0,
    Number(data?.controller?.twiId),
  )

  const {
    data: dataLineUnitDecoder,
    isFetching: loadingDecoder,
    refetch: fetchDecoder,
  } = useStationLineUnitDecoder(
    Number(interfaceId),
    Number(data?.controller?.address) || 0,
    Number(data?.controller?.index) || 0,
    Number(data?.controller?.twiId),
  )

  const { data: dataError } = useErrorcode(interfaceId, data?.errorCode)

  // Real-time: refresh this station immediately on SignalR StationUpdate,
  // instead of waiting for the 30s poll. Errors (e.g. line-unit faults raised
  // when the station starts) then appear as soon as the backend pushes.
  useEffect(() => {
    if (!id || !interfaceId) return

    const groupName = getGroupName(Number(interfaceId))
    let connection: HubConnection | undefined
    let isMounted = true
    // StationUpdate is group-targeted; join the interface group first. Payload
    // is a single object { interfaceId, items: [{ id, status }] }.
    const handleStationUpdate = (stationUpdate: StationUpdateSignalR) => {
      if (Number(stationUpdate?.interfaceId) !== Number(interfaceId)) return
      const changed = stationUpdate?.items?.some(
        (it) => stationUpdateItemId(it) === Number(id),
      )
      if (!changed) return
      dispatch(fetchStationOverview({ interfaceId, stationId: id }))
      fetchControl()
      fetchDecoder()
    }

    const startSignalR = async () => {
      try {
        connection = await getHubConnect()
        if (!isMounted) return
        await joinGroup(groupName)
        connection.on(ActionSignalR.STATION_UPDATE, handleStationUpdate)
      } catch (error) {
        console.error('Station overview SignalR subscription failed:', error)
      }
    }

    startSignalR()

    return () => {
      isMounted = false
      // Remove only THIS handler — the hub connection is shared.
      connection?.off(ActionSignalR.STATION_UPDATE, handleStationUpdate)
      leaveGroup(groupName)
    }
  }, [
    id,
    interfaceId,
    dispatch,
    getHubConnect,
    joinGroup,
    leaveGroup,
    fetchControl,
    fetchDecoder,
  ])

  const openEditStation = (id: string) => {
    openSidebar()
    setSidebarView({ type: 'STATIONS_VIEW', id })
  }

  const { mutate: resetById } = useStationResetById()
  // const [toastMessage, setToastMessage] = useState('')
  const [arrayReset, setArrayReset] = useState<any>([])
  const [checkReset, setCheckReset] = useState(false)
  const refetchStationOverview = () => {
    if (!id || !interfaceId) return
    // Refetch immediately + after 2s (server may need time to process state change)
    dispatch(fetchStationOverview({ interfaceId, stationId: id }))
    setTimeout(() => dispatch(fetchStationOverview({ interfaceId, stationId: id })), 2000)
  }

  const { mutate: offStations, isLoading: loadingOff } = useSearchStationOff({
    onSuccess: () => {
      setOptimisticStatus('Off')
      refetchStationOverview()
    },
  })

  useEffect(() => {
    const getKeyReset = localStorage.getItem(LOCAL_KEY_ERROR)
    if (getKeyReset) {
      setArrayReset(JSON.parse(getKeyReset))
      setCheckReset(JSON.parse(getKeyReset).includes(id))
      if (!dataError) {
        const resData = JSON.parse(getKeyReset).filter(
          (item: any) => item !== id,
        )
        setArrayReset(resData)
        localStorage.setItem(LOCAL_KEY_ERROR, JSON.stringify(resData))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleOff = (id: number, active: boolean) => {
    // ViewOnly users cannot turn off stations
    if (isViewOnly) return

    if (active === false) {
      toast.error('Station inactive')
    } else {
      offStations({
        id: id,
        interfaceId: interfaceId,
      })
    }
  }

  const handleOn = (id: number, active: boolean) => {
    // ViewOnly users cannot turn on stations
    if (isViewOnly) return

    if (active === false) {
      toast.error('Station inactive')
    } else {
      openModal()
      setModalView({
        type: 'STATION_MODAL',
        id: id.toString(),
        stateStation: 'On',
        onSuccess: () => setOptimisticStatus('On'),
      })
    }
  }

  const handleResetId = () => {
    // ViewOnly users cannot acknowledge errors
    if (isViewOnly) return

    resetById({
      interfaceId: interfaceId,
      id: id,
    })
    const arrayNew = [...arrayReset, id]
    setArrayReset(arrayNew)
    localStorage.setItem(LOCAL_KEY_ERROR, JSON.stringify(arrayNew))
    closeSidebar()
  }

  const handleRefresh = (type: string) => {
    if (type === 'decoder') {
      fetchDecoder()
      setDateDecoder(new Date())
    }
    if (type === 'control') {
      fetchControl()
      setDateControl(new Date())
    }
  }
  const showData = (value: any, unit: string) => {
    if (value === null || value === undefined) return '-'
    return `${value} ${unit}`
  }

  const getStatusByName = (data: any) => {
    if (
      data?.stateName !== STATION_STATE_NAME.ERROR &&
      data?.stateName !== STATION_STATE_NAME.IDLING &&
      data?.stateName !== STATION_STATE_NAME.INACTIVE
    ) {
      return 'On'
    }
    return 'Off'
  }

  const isCanStopStation = useMemo(() => {
    if (optimisticStatus === 'On') return true
    if (optimisticStatus === 'Off') return false
    if (getStatusByName(data) === 'On') return true
    if (data?.stateName === STATION_STATE_NAME.WAIT_START) return true
    return false
  }, [data, optimisticStatus])

  // Clear optimistic status when Redux refetch returns new data
  const prevStateNameRef = useRef(data?.stateName)
  useEffect(() => {
    if (optimisticStatus && data?.stateName !== prevStateNameRef.current) {
      setOptimisticStatus(null)
    }
    prevStateNameRef.current = data?.stateName
  }, [data?.stateName, optimisticStatus])

  // SCRUM-1157 — a station switched to non-irrigation keeps its old pipe,
  // plant, soil and emitter on the server (the save leaves them in place), so
  // they are hidden here as the station grid hides them, not shown as if the
  // station still irrigated.
  const isNonIrrigation = Number(data?.type) === 1
  const irrigationOnly = (value: ReactNode) => (isNonIrrigation ? '-' : value)

  return (
    <Container
      sx={{
        width: '450px',
        padding: '28px 0',
        paddingLeft: '40px !important',
        paddingRight: '30px !important',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          marginBottom: '20px',
        }}
      >
        <Typography color="text.main">{t('stationDetails')}</Typography>
        <Stack direction="row">
          <Button
            variant="secondary"
            sx={{ marginRight: '7px', minWidth: '85px' }}
            onClick={() => closeSidebar()}
          >
            {t('commonActionsClose')}
          </Button>
          <Button
            disabled={isViewOnly}
            variant={isViewOnly ? 'outlined' : 'primary'}
            sx={{
              minWidth: '72px',
              opacity: isViewOnly ? 0.6 : 1,
              cursor: isViewOnly ? 'not-allowed' : 'pointer',
            }}
            onClick={() => openEditStation(id as string)}
          >
            {t('commonActionsEdit')}
          </Button>
        </Stack>
      </Stack>
      {loading && <StationOverviewSkeleton />}
      {!loading && !data && <Typography>{t('noData')}</Typography>}
      {data && (
        <Stack direction="column">
          {
            /* Thumbnail */
            data?.pictureUrl && (
              <Image
                src={`${data?.pictureUrl}`}
                title={data?.pictureFileName}
                width="0"
                height="0"
                ratio={57}
                sx={{
                  borderRadius: '5px',
                  overflow: 'hidden',
                  backgroundColor: '#f8f8f8',
                }}
              />
            )
          }
          {/* Title */}
          <Typography mb="5px" mt="20px" title={''} noWrap width="100%">
            {data ? resolveStationName(data) : ''}
          </Typography>
          <Typography mb="5px" title={''} noWrap width="100%">
            {t('lastUpdate')} {formatDateByTimezone(data?.lastUpdate)}
          </Typography>
          <Stack direction="row" justifyContent="space-between">
            <Typography className="stt-ovv__infobox__title">
              {t('commonLabelsStatus')}
            </Typography>
            <Typography>{data?.stateName}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" mt="5px">
            <Typography className="stt-ovv__infobox__title">
              {t('formsLabelsArea')}
            </Typography>
            <Typography>{data?.subArea?.parentArea?.name}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" mt="5px">
            <Typography className="stt-ovv__infobox__title">
              {t('formsLabelsSubArea')}
            </Typography>
            <Typography>{data?.subArea?.name}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" mt="5px">
            <Typography className="stt-ovv__infobox__title">
              {t('Station Type')}
            </Typography>
            <Typography>{data?.type == '1' ? 'Non-Irrigation': 'Irrigation'}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" mt="5px">
            <Typography className="stt-ovv__infobox__title">
              {t('formsLabelsMicroClimate')}
            </Typography>
            <Typography>{data?.etZone?.name ?? '-'}</Typography>
          </Stack>
          {/* Buttons*/}
          <Stack
            direction="row"
            gap="8px"
            justifyContent="space-between"
            my="18px"
            sx={{
              '& .MuiButtonBase-root': {
                padding: '5px 20px',
                flex: '1 1 auto',
              },
            }}
          >
            <Button
              disabled={isViewOnly}
              size="small"
              variant={isViewOnly ? 'outlined' : 'secondary'}
              color="secondary"
              onClick={() => handleOn(data?.id, data?.active)}
              sx={{
                opacity: isCanStopStation ? 0.5 : 1,
                userSelect: 'none',
                pointerEvents: isCanStopStation ? 'none' : 'auto',
              }}
            >
              {t('commonActionsOn')}
            </Button>

            <LoadingButton
              disabled={isViewOnly}
              loading={loadingOff}
              size="small"
              variant={isViewOnly ? 'outlined' : 'secondary'}
              onClick={() => handleOff(data?.id, data?.active)}
              sx={{
                opacity: !isCanStopStation ? 0.5 : 1,
                userSelect: 'none',
                pointerEvents: !isCanStopStation ? 'none' : 'auto',
              }}
            >
              {t('commonActionsOff')}
            </LoadingButton>
          </Stack>
          <Box className="stt-ovv__infobox" sx={infoBoxStyle}>
            <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">
                {t('errorCode')}
              </Typography>
              <Typography>{data?.errorCode}</Typography>
            </Stack>
            {data?.errorCode !== 0 && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ p: 1, background: '#FFF2F2', borderRadius: '5px' }}
              >
                <Typography className="stt-ovv__infobox__title_error">
                  {dataError?.appName}
                </Typography>
                <IconButton onClick={handleResetId} disabled={checkReset}>
                  <ResetIcon
                    sx={{
                      transform: 'scale(2)',
                      opacity: checkReset ? '.5' : '1',
                    }}
                  />
                </IconButton>
              </Stack>
            )}
          </Box>
          {/* Info */}
          <Box className="stt-ovv__infobox stt-ovv--color" sx={infoBoxStyle}>
            <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">
                Default Station RunTime
              </Typography>
              <Typography>{data?.defaultRunTime}</Typography>
            </Stack>
            {data?.controller?.twiCustomName && (
              <Stack direction="row" className="stt-ovv__infobox__row">
                <Typography className="stt-ovv__infobox__title">
                  Controller Name
                </Typography>
                <Typography>{data?.controller?.twiCustomName}</Typography>
              </Stack>
            )}

            <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">
                Address
              </Typography>
              <Typography>{data?.controller?.address}</Typography>
            </Stack>
            <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">Index</Typography>
              <Typography>{data?.controller?.index}</Typography>
            </Stack>
            <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">
                Priority
              </Typography>
              <Typography>{irrigationOnly(data?.priority)}</Typography>
            </Stack>
            <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">Pipe</Typography>
              <Typography>{irrigationOnly(data?.pipe?.description)}</Typography>
            </Stack>
            <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">Plant</Typography>
              <Typography>{irrigationOnly(data?.plant?.description)}</Typography>
            </Stack>
            <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">Soil</Typography>
              <Typography>{irrigationOnly(data?.soil?.description)}</Typography>
            </Stack>
            <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">
                {t('formsLabelsEmitter')}
              </Typography>
              <Typography>{irrigationOnly(data?.emitter?.emitter)}</Typography>
            </Stack>
          </Box>
          {/* Info */}
          <Box className="stt-ovv__infobox" sx={infoBoxStyle}>
            <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">
                {t('formsLabelsWaterRequired')} ({convertLabelUnit('mm')})
              </Typography>
              <Typography>
                {irrigationOnly(convertFromMetric(data?.waterRequired, 'mm'))}
              </Typography>
            </Stack>
            {/* <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">
                Surface Accumulation ({convertLabelUnit('mm')})
              </Typography>
              <Typography>
                {convertFromMetric(data?.surfaceAccumulation, 'mm')}
              </Typography>
            </Stack> */}
            <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">
                {t('formsLabelsPrecipitationRate')} ({convertLabelUnit('mm/h')})
              </Typography>
              <Typography>
                {irrigationOnly(
                  convertFromMetric(data?.precipitationRate, 'mm/h'),
                )}
              </Typography>
            </Stack>
            {/* <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">
                Plant Available Water ({convertLabelUnit('mm')})
              </Typography>
              <Typography>
                {convertFromMetric(data?.plantAvailWater, 'mm')}
              </Typography>
            </Stack> */}
            <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">
                Expected Flow ({convertLabelUnit('l/m')})
              </Typography>
              <Typography>
                {irrigationOnly(convertFromMetric(data?.expectedFlow, 'l/m'))}
              </Typography>
            </Stack>
            {/* <Stack direction="row" className="stt-ovv__infobox__row">
              <Typography className="stt-ovv__infobox__title">
                Landscape Factor(%)
              </Typography>
              <Typography>{data?.landscapeFactor}</Typography>
            </Stack> */}
          </Box>
          {/* Info */}

          <Box className="stt-ovv__infobox" sx={infoBoxStyle}>
            <Stack
              direction="row"
              className="stt-ovv__infobox__row"
              sx={{ mb: '16px !important' }}
            >
              <Stack direction={'column'}>
                <Typography
                  className="stt-ovv__infobox__title"
                  sx={{
                    fontWeight: '600 !important',
                    textTransform: 'uppercase',
                    maxWidth: '100% !important',
                  }}
                >
                  Line Unit Readings
                </Typography>
                {dateUpdateControl && !loadingControl && (
                  <Typography mt="5px" mb="5px" title={''} noWrap width="100%">
                    Last update{' '}
                    {formatDateByTimezone(dateUpdateControl.toISOString())}
                  </Typography>
                )}
              </Stack>
              <LoadingButton
                variant="contained"
                type="button"
                size="small"
                loading={loadingControl}
                onClick={() => handleRefresh('control')}
                sx={{ minWidth: '40px', height: '28px' }}
                color="secondary"
                disabled={isViewOnly}
              >
                <Refresh />
              </LoadingButton>
            </Stack>
            {loadingControl ? (
              <Stack
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 120,
                }}
              >
                <CircularProgress size={25} />
              </Stack>
            ) : (
              <>
                <Stack direction="row" className="stt-ovv__infobox__row">
                  <Typography className="stt-ovv__infobox__title">
                    {t('lineResistance')}
                  </Typography>
                  <Typography>
                    {showData(dataLineUnitControl?.resistance, 'ohm')}
                  </Typography>
                </Stack>
                <Stack direction="row" className="stt-ovv__infobox__row">
                  <Typography className="stt-ovv__infobox__title">
                    {t('operationsTemperature')}{' '}
                  </Typography>
                  <Typography>
                    {dataLineUnitControl?.temperature
                      ? `${convertFromMetric(
                          dataLineUnitControl.temperature,
                          'C',
                        )} °${convertLabelUnit('C')}`
                      : '-'}
                  </Typography>
                </Stack>
                <Stack direction="row" className="stt-ovv__infobox__row">
                  <Typography className="stt-ovv__infobox__title">
                    Voltage
                  </Typography>
                  <Typography>
                    {showData(dataLineUnitControl?.voltage, 'mV')}
                  </Typography>
                </Stack>
                <Stack direction="row" className="stt-ovv__infobox__row">
                  <Typography className="stt-ovv__infobox__title">
                    Leak{' '}
                  </Typography>
                  <Typography>
                    {showData(dataLineUnitControl?.leak, 'mA')}
                  </Typography>
                </Stack>
              </>
            )}
          </Box>

          <Box className="stt-ovv__infobox" sx={infoBoxStyle}>
            <Stack
              direction="row"
              className="stt-ovv__infobox__row"
              sx={{ mb: '16px !important' }}
            >
              <Stack direction={'column'}>
                <Typography
                  className="stt-ovv__infobox__title"
                  sx={{
                    fontWeight: '600 !important',
                    textTransform: 'uppercase',
                    maxWidth: '100% !important',
                  }}
                >
                  {t('decoderReadings')}
                </Typography>
                {dateUpdateDecode && !loadingDecoder && (
                  <Typography mt="5px" mb="5px" title={''} noWrap width="100%">
                    {t('lastUpdate')}{' '}
                    {formatDateByTimezone(dateUpdateDecode.toISOString())}
                  </Typography>
                )}
              </Stack>
              <LoadingButton
                variant="contained"
                type="button"
                size="small"
                loading={loadingDecoder}
                onClick={() => handleRefresh('decoder')}
                sx={{ minWidth: '40px', height: '28px' }}
                color="secondary"
                disabled={isViewOnly}
              >
                <Refresh />
              </LoadingButton>
            </Stack>
            {loadingDecoder ? (
              <Stack
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 120,
                }}
              >
                <CircularProgress size={25} />
              </Stack>
            ) : (
              <>
                <Stack direction="row" className="stt-ovv__infobox__row">
                  <Typography className="stt-ovv__infobox__title">
                    Value
                  </Typography>
                  <Typography>
                    {formatDecoderValue(dataLineUnitDecoder?.rawValue, t)}
                  </Typography>
                </Stack>
                <Stack direction="row" className="stt-ovv__infobox__row">
                  <Typography className="stt-ovv__infobox__title">
                    Switch Parameters
                  </Typography>

                  <Typography>
                    {dataLineUnitDecoder?.switchParameters
                      ? `${showData(
                          dataLineUnitDecoder?.switchParameters?.inrush,
                          'V',
                        )}, ${showData(
                          dataLineUnitDecoder?.switchParameters?.inrushTime,
                          'ms',
                        )}, ${showData(
                          dataLineUnitDecoder?.switchParameters?.hold,
                          'V',
                        )}`
                      : '-'}
                  </Typography>
                </Stack>
                <Stack direction="row" className="stt-ovv__infobox__row">
                  <Typography className="stt-ovv__infobox__title">
                    {t('commonUnitsResistance')}
                  </Typography>
                  <Typography>
                    {showData(dataLineUnitDecoder?.resistance, 'ohm')}
                  </Typography>
                </Stack>
                <Stack direction="row" className="stt-ovv__infobox__row">
                  <Typography className="stt-ovv__infobox__title">
                    {t('commonUnitsInductance')}
                  </Typography>
                  <Typography>
                    {showData(dataLineUnitDecoder?.inductance, 'mH')}
                  </Typography>
                </Stack>
              </>
            )}
          </Box>
        </Stack>
      )}
    </Container>
  )
}

export default StationOverview

const StationOverviewSkeleton = () => {
  return (
    <Stack direction="column">
      <Skeleton variant="rounded" height={218} />
      <Skeleton variant="text" sx={{ margin: '20px 0 5px' }} />
      <Skeleton variant="text" sx={{ margin: '0 0 5px' }} />
      <Skeleton variant="text" />
      <Skeleton variant="rounded" height={26} sx={{ margin: '18px 0 18px' }} />
      <Skeleton variant="rounded" height={200} />
    </Stack>
  )
}
